import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TurniDefaultInterface,
  TurniItemInterface,
} from '../interfaces/default-turni-point-of-sale';
import {
  checkAllDefaultHours,
  checkDefaultHoursValue,
} from '../components/PopUp/utils/check-default-hours';
import { RootState } from '../../../store';

/**
 * Tipo per i giorni della settimana
 */

/**
 * Tipo per i tipi di turno
 */
type TurnoType = 'calendar' | 'shift';

/**
 * Tipo per i numeri di intervallo
 */
type RangeNumber = 1 | 2;

/**
 * Crea una chiave tipizzata per gli orari default
 */
type DefaultHoursKey = `${TurnoType}${string}`;

/**
 * Crea una chiave tipizzata per gli errori
 */
type ErrorDefaultHoursKey = `${TurnoType}${string}${RangeNumber}`;

/**
 * Tipo per la mappa degli errori
 */
type ErrorDefaultHoursMap = Record<ErrorDefaultHoursKey, boolean>;

/**
 * Interfaccia per lo stato degli orari predefiniti
 */
export interface TurniDefaultHoursInterface {
  defaultHours: TurniDefaultInterface;
  errorDefaultHours: ErrorDefaultHoursMap;
  importDefaultHoursPopUp: boolean;
}

/**
 * Genera la struttura iniziale degli errori
 */
function generateInitialErrorMap(): ErrorDefaultHoursMap {
  const turniTypes: TurnoType[] = ['calendar', 'shift'];
  const days: string[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  const ranges: RangeNumber[] = [1, 2];

  const errors = {} as ErrorDefaultHoursMap;

  for (const turniType of turniTypes) {
    for (const day of days) {
      for (const range of ranges) {
        errors[`${turniType}${day}${range}`] = false;
      }
    }
  }

  return errors;
}

/**
 * Genera la struttura iniziale degli orari predefiniti
 */
function generateInitialDefaultHours(): TurniDefaultInterface {
  const turniTypes: TurnoType[] = ['calendar', 'shift'];
  const days: string[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  const defaultHours = {} as TurniDefaultInterface;

  for (const turniType of turniTypes) {
    for (const day of days) {
      defaultHours[`${turniType}${day}` as DefaultHoursKey] = null;
    }
  }

  return defaultHours;
}

/**
 * Stato iniziale degli orari predefiniti
 */
const initialState: TurniDefaultHoursInterface = {
  defaultHours: generateInitialDefaultHours(),
  errorDefaultHours: generateInitialErrorMap(),
  importDefaultHoursPopUp: false,
};

/**
 * Slice Redux per gli orari predefiniti dei punti vendita
 */
const PointOfSaleDefaultHoursSlice = createSlice({
  name: 'PointOfSaleDefaultHours',
  initialState,
  reducers: {
    /**
     * Imposta gli orari predefiniti e aggiorna gli errori associati
     */
    setDefaultHours(
      state: TurniDefaultHoursInterface,
      action: PayloadAction<TurniDefaultInterface>,
    ) {
      state.defaultHours = action.payload;
      state.errorDefaultHours = checkAllDefaultHours(action.payload) as any;
    },

    /**
     * Imposta il valore di un orario predefinito e aggiorna gli errori associati
     */
    setDefaultHoursValue(
      state: TurniDefaultHoursInterface,
      action: PayloadAction<{
        defaultHoursValue: TurniItemInterface;
        day: string;
        turniType: TurnoType;
      }>,
    ) {
      const { turniType, day, defaultHoursValue } = action.payload;
      const key = `${turniType}${day}` as DefaultHoursKey;

      // Aggiorna il valore dell'orario predefinito
      state.defaultHours[key] = defaultHoursValue;

      // Aggiorna tutti gli errori associati a questo giorno
      updateErrorsForDay(state, turniType, day);
    },

    /**
     * Imposta lo stato del popup per importare orari predefiniti
     */
    setImportDefaultHoursPopUp(
      state: TurniDefaultHoursInterface,
      action: PayloadAction<boolean>,
    ) {
      state.importDefaultHoursPopUp = action.payload;
    },

    /**
     * Reimposta lo stato agli valori iniziali
     */
    resetDefaultHours() {
      return initialState;
    },
  },
});

/**
 * Funzione helper per aggiornare gli errori per un giorno specifico
 */
function updateErrorsForDay(
  state: TurniDefaultHoursInterface,
  turniType: TurnoType,
  day: string,
) {
  // Aggiorna gli errori per tutti i turni e intervalli di questo giorno
  const turniTypes: TurnoType[] = ['calendar', 'shift'];
  const ranges: RangeNumber[] = [1, 2];

  for (const type of turniTypes) {
    for (const range of ranges) {
      const errorKey = `${type}${day}${range}` as ErrorDefaultHoursKey;
      state.errorDefaultHours[errorKey] = checkDefaultHoursValue(
        type,
        range,
        day,
        state.defaultHours,
      );
    }
  }
}

export const {
  setDefaultHours,
  setImportDefaultHoursPopUp,
  setDefaultHoursValue,
  resetDefaultHours,
} = PointOfSaleDefaultHoursSlice.actions;

/**
 * Selettore per ottenere lo stato di errore per un intervallo specifico
 */
export const defaultHourValueError = (
  state: RootState,
  turniType: TurnoType,
  day: string,
  rangeNumber: RangeNumber,
): boolean => {
  const key = `${turniType}${day}${rangeNumber}` as ErrorDefaultHoursKey;
  return state.bluBookingPointOfSaleDefaultHours.errorDefaultHours[key];
};

/**
 * Selettore per ottenere il valore di un orario predefinito
 */
export const defaultHourValue = (
  state: RootState,
  turniType: TurnoType,
  day: string,
): TurniItemInterface | null => {
  const key = `${turniType}${day}` as DefaultHoursKey;
  console.log(
    'defaultHourValue',
    key,
    state.bluBookingPointOfSaleDefaultHours.defaultHours,
  );
  return state.bluBookingPointOfSaleDefaultHours.defaultHours[key];
};

/**
 * Selettore per determinare se ci sono errori nei dati degli orari predefiniti
 */
export const hasErrordefaultHourValue = (state: RootState): boolean =>
  Object.values(state.bluBookingPointOfSaleDefaultHours.errorDefaultHours).some(
    (error) => error,
  );

export default PointOfSaleDefaultHoursSlice.reducer;
